import { useParams } from "react-router-dom";
import ApiStatus from "../apiStatus";
import { useDeleteUser, useFetchUser, useUpdateUser } from "../hooks/UserHooks";
import ValidationSummary from "../ValidationSummary";
import UserForm from "./UserForm";
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useContext, useState } from "react";
import { Modal, Button } from 'react-bootstrap';
import { AuthContext } from "../AuthContext";
import { UserDTO } from "../types/userDTO";

const UserEdit = () => {
  const { t } = useTranslation();
  const { roles } = useContext(AuthContext);
  const { organisationId, userId } = useParams<{ organisationId: string, userId: string }>();
  if (!organisationId) throw new Error("Missing organisationId from params");
  if (!userId) throw Error("Need a user id");
  const { data, status, isSuccess, isFetching } = useFetchUser(organisationId, userId);
  const updateUserMutation = useUpdateUser(organisationId);
  const deleteUserMutation = useDeleteUser(organisationId);
  const [userToDelete, setUserToDelete] = useState<UserDTO | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const canDelete = roles.includes("SysAdmin") || roles.includes("OptiviAdmin");

  const handleUserDelete = (user: UserDTO) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    if (!userToDelete) return;

    deleteUserMutation.mutate(userToDelete, {
      onSuccess: () => {
        setShowDeleteModal(false);
        toast.success(t('user.deleteSuccess'));
        history.back();
      },
      onError: () => {
        setShowDeleteModal(false);
        toast.error(t('user.deleteError'));
      }
    });
  };

  if (!isSuccess) return <ApiStatus status={status} />;
  if (isFetching) return <ApiStatus status="pending" />;

  const userWithLocalDates = {
    ...data,
    password: '',
    validFrom: new Date(data.validFrom + 'Z'),
    validTo: new Date(data.validTo + 'Z'),
    lastModified: new Date(data.lastModified + 'Z')
  };

  return (
    <>
      {updateUserMutation.isError && (
        <ValidationSummary error={updateUserMutation.error} />
      )}
      {updateUserMutation.isPending ? (
        <div className="spinner-container" style={{ textAlign: 'center', marginTop: '20px' }}>
          <ClipLoader size={50} color={"#123abc"} loading={true} />
          <p>{t('user.editWait')}</p>
        </div>
      ) : (
        <UserForm
          headerText={t('user.edit')}
          user={userWithLocalDates}
          submitted={(user) => {
            updateUserMutation.mutate(user, {
              onSuccess: () => {
                toast.success(t('user.editSuccess'));
                history.back();
              },
              onError: () => {
                toast.error(t('user.editError'));
              }
            });
          }}
          deleted={canDelete ? handleUserDelete : undefined}
        />
      )}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('common.confirmation')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('user.deleteConfirmation')}</p>
          {userToDelete && (
            <>
              <div><strong>{t('common.username')}:</strong> {userToDelete.userName}</div>
              <div><strong>{t('user.firstName')}:</strong> {userToDelete.firstName}</div>
              <div><strong>{t('user.lastName')}:</strong> {userToDelete.lastName}</div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleDelete} disabled={deleteUserMutation.isPending}>
            {t('buttons.delete')}
          </Button>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteUserMutation.isPending}>
            {t('buttons.cancel')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserEdit;
